import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { certifications } from "@/data/products";
import { ArrowUpRight, Compass, Globe, Handshake, Sparkles, Leaf, Shirt, Apple, Wheat, HardHat, Flower2 } from "lucide-react";

const verticals = [
  { icon: Leaf, title: "Agro Solutions Division", subtitle: "Revolutionizing Agricultural Productivity",
    desc: "Organic and precision-nutrition fertilisers, bio-stimulants, soil-health blends and crop protection products formulated for high-yield, sustainable farming." },
  { icon: Shirt, title: "Textile Excellence Division", subtitle: "Crafting Sustainable Fashion Foundations",
    desc: "Premium silks, OEKO-TEX cotton, GOTS-certified organic fabrics, GI-tagged heritage textiles and full-package OEM apparel manufacturing." },
  { icon: Apple, title: "Fresh Produce Division", subtitle: "Farm-to-Port Freshness, Globally",
    desc: "Alphonso mangoes, Bhagwa pomegranates, Nashik onions and India's finest exotic fruits and vegetables — reefer-shipped with full cold-chain traceability." },
  { icon: Wheat, title: "Pulses & Grains Division", subtitle: "Sustaining Global Nutrition",
    desc: "Pusa Basmati 1121, GI-tagged regional rice, all major pulses, oilseeds and processed grain products — feeding 40+ nations through certified supply chains." },
  { icon: HardHat, title: "Infrastructure Materials Division", subtitle: "Building Sustainable Futures",
    desc: "Indian marble, granite, sandstone, structural steel, cement and engineered building materials — quarry-direct, mill-direct, ASTM/EN compliant." },
  { icon: Flower2, title: "Flora Division", subtitle: "Bringing Nature's Beauty Worldwide",
    desc: "Live ornamental trees, palms, shrubs, ground covers, medicinal herbs and essential oils — phytosanitary-cleared nursery stock and certified botanical extracts." },
];

const leadership = [
  {
    name: "Sandeep Rajat",
    role: "CEO · Founder",
    initials: "SR",
    bio: "Founder and Chief Executive of AR Overseas Exports. Two decades in international trade, sourcing partnerships and export logistics — architect of the company's farm-to-port and quarry-to-port programs across 40+ nations.",
  },
  {
    name: "Om Prakash",
    role: "Operations Director",
    initials: "OP",
    bio: "Heads end-to-end operations — supplier compliance, quality assurance, container logistics and destination-country documentation. Custodian of every shipment that leaves an Indian port under the AR Overseas seal.",
  },
];

export default function About() {
  return (
    <main className="pb-24" data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden bg-[#0D1A12]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1605647540924-852290f6b0d5?auto=format&fit=crop&w=2400&q=90"
            alt="Global trade backdrop"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 royal-gradient opacity-85" />
          <div className="absolute inset-0 holographic mix-blend-overlay opacity-50" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 text-[#F9F8F6]">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[10px] uppercase tracking-[0.4em] gold-text font-medium mb-5">Our Profile · Pioneering Global Trade</p>
            <h1 className="font-serif text-6xl md:text-8xl font-light tracking-tighter leading-[0.95]">
              A New Delhi<br />trading house<br /><em className="gold-text not-italic">for the world.</em>
            </h1>
            <div className="mt-10 max-w-3xl space-y-5 text-lg md:text-xl font-light leading-relaxed opacity-90">
              <p>
                AR Overseas Exports Pvt. Ltd. stands as a dynamic force in global commerce — redefining industry standards
                across <span className="text-[#D4AF37]">six strategic verticals</span> and a footprint that spans more than
                <span className="text-[#D4AF37]"> 40 countries</span>.
              </p>
              <p>
                We exist to be the trusted bridge between India's premium resources and the world's most demanding markets —
                anchored in innovation, sustainability and uncompromising quality.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CORPORATE IDENTITY */}
      <section className="py-24 md:py-32 bg-[#F9F8F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Corporate Identity</p>
            <h2 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[1.05]">
              Grounded in <em className="text-[#B85C38]">origin.</em><br/>Global by <em className="text-[#2C4532]">design.</em>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[#4A4D4A] text-lg font-light leading-relaxed">
            <p>
              We work with a deep bench of mills, farm cooperatives, quarry operators, handloom clusters and factories
              across India. Every shipment is backed by independent quality validation, destination-specific compliance and
              the documentation protocols required by modern global trade.
            </p>
            <p>
              From Pusa Basmati 1121 and Alphonso mango pulp to Makrana marble, structural steel and Banarasi silk — our
              eleven-sector portfolio is engineered so buyers can consolidate India sourcing under a single, accountable
              partner. One desk. One contract. One signature.
            </p>
            <p className="text-[#1A1B1A] font-normal italic font-serif text-2xl pt-2">
              "Pioneering Global Trade — connecting India's heritage to the world's table."
            </p>
          </div>
        </div>
      </section>

      {/* STRATEGIC VERTICALS */}
      <section className="py-24 md:py-32 bg-[#EAE6DF]/40 border-y border-[#EAE6DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Strategic Business Verticals</p>
            <h2 className="font-serif text-5xl md:text-6xl font-light tracking-tighter">Six divisions.<br/><em className="text-[#B85C38]">One signature.</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {verticals.map(({ icon: Icon, title, subtitle, desc }) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl border border-[#EAE6DF] p-8 hover:border-[#D4AF37] hover:shadow-lg transition-all duration-500"
              >
                <div className="h-12 w-12 rounded-full bg-[#2C4532] text-[#D4AF37] flex items-center justify-center mb-5">
                  <Icon size={20} />
                </div>
                <h3 className="font-serif text-2xl text-[#2C4532]">{title}</h3>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#B85C38] mt-1">{subtitle}</p>
                <p className="mt-4 text-sm text-[#4A4D4A] font-light leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Leadership</p>
            <h2 className="font-serif text-5xl md:text-6xl font-light tracking-tighter">The people<br/><em className="text-[#2C4532]">behind every shipment.</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {leadership.map((p) => (
              <div key={p.name} className="rounded-3xl bg-white border border-[#EAE6DF] p-10 hover:border-[#D4AF37] hover:shadow-lg transition-all">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{p.role}</p>
                    <h3 className="font-serif text-3xl text-[#2C4532] mt-2">{p.name}</h3>
                  </div>
                  <span
                    className="font-serif text-2xl shrink-0 h-16 w-16 rounded-full flex items-center justify-center"
                    style={{
                      background: "radial-gradient(circle at 30% 30%, #2C4532 0%, #16291C 70%, #0d1d12 100%)",
                      color: "#D4AF37",
                      border: "1px solid rgba(212,175,55,0.5)",
                    }}
                  >
                    {p.initials}
                  </span>
                </div>
                <div className="gold-divider my-6" />
                <p className="text-[#4A4D4A] font-light leading-relaxed">{p.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-20 bg-[#0D1A12] text-[#F9F8F6] relative overflow-hidden">
        <div className="absolute inset-0 holographic opacity-25 blur-3xl" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Compass, t: "Origin Intelligence", d: "Direct relationships with growers, mills and quarries — no layered middlemen." },
              { icon: Globe, t: "40+ Country Reach", d: "Active buyers across Middle East, Africa, Europe, Asia-Pacific, Americas & CIS." },
              { icon: Handshake, t: "Contract Reliability", d: "Repeat buyers drive 70%+ of our volume — quality, timeliness, trust." },
              { icon: Sparkles, t: "Premium + Mass", d: "From GI-tagged gourmet to volume-driving staples — a single accountable desk." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="glass-dark rounded-2xl p-6 border border-[#D4AF37]/20">
                <Icon size={22} className="text-[#D4AF37]" />
                <h4 className="mt-4 font-serif text-2xl">{t}</h4>
                <p className="mt-2 text-sm opacity-80 font-light leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTS */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-8 text-center">Buyer-Trusted Certifications</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {certifications.map((c) => (
              <div key={c.code} className="text-center p-4 rounded-xl bg-white/60 border border-[#EAE6DF]">
                <div className="font-serif text-xl text-[#2C4532]">{c.code}</div>
                <div className="mt-1 text-[10px] text-[#4A4D4A] uppercase tracking-wider">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 text-center">
        <h3 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Ready to begin?</h3>
        <p className="mt-4 text-[#4A4D4A]">Send a detailed RFQ — we reply within 12 business hours with pricing & documentation.</p>
        <Link to="/contact" data-testid="about-cta" className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#2C4532] text-[#F9F8F6] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#1F3124]">
          Request a Quote <ArrowUpRight size={14} />
        </Link>
      </section>
    </main>
  );
}
